import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SpeakerIcon from '@mui/icons-material/Speaker';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import EqualizerIcon from '@mui/icons-material/Equalizer'; 
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import AlbumIcon from '@mui/icons-material/Album';
import './Form.css';
import './HiFiSetup.css';

const HiFiSetupForm = () => {
  const navigate = useNavigate();
  const [setupId, setSetupId] = useState(null);
  const [speakers, setSpeakers] = useState('');
  const [amplifier, setAmplifier] = useState('');
  const [preamp, setPreamp] = useState('');
  const [dac, setDac] = useState('');
  const [sources, setSources] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Always get the latest user and token from localStorage.
  const getUser = () => JSON.parse(localStorage.getItem('user'));
  const getToken = () => localStorage.getItem('token');

  // On mount, fetch an existing setup (if any) only once.
  useEffect(() => {
    const user = getUser();
    const token = getToken();
    if (!user || !token) return;

    async function fetchSetup() {
      try {
        const response = await fetch(`/api/hifisetups?userId=${user.id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (response.ok) {
          const setups = await response.json();
          if (setups && setups.length > 0) {
            const setup = setups[0];
            setSetupId(setup.id);
            setSpeakers(setup.speakers ? (Array.isArray(setup.speakers) ? setup.speakers.join(', ') : setup.speakers) : '');
            setAmplifier(setup.amplifier ? (Array.isArray(setup.amplifier) ? setup.amplifier.join(', ') : setup.amplifier) : '');
            setPreamp(setup.preamp ? (Array.isArray(setup.preamp) ? setup.preamp.join(', ') : setup.preamp) : '');
            setDac(setup.dac ? (Array.isArray(setup.dac) ? setup.dac.join(', ') : setup.dac) : '');
            setSources(setup.sources ? (Array.isArray(setup.sources) ? setup.sources.join(', ') : setup.sources) : '');
          }
        } else {
          console.error('Error fetching setup:', await response.text());
        }
      } catch (err) {
        console.error('Error fetching setup:', err);
      }
    }
    fetchSetup();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const currentUser = getUser();
    const currentToken = getToken();
    if (!currentUser || !currentToken) {
      setError('Benutzer nicht authentifiziert. Bitte loggen Sie sich ein.');
      setLoading(false);
      return;
    }

    // Parse CSV fields into arrays.
    const speakersArray = speakers.split(',').map(item => item.trim()).filter(item => item);
    const amplifierArray = amplifier.split(',').map(item => item.trim()).filter(item => item);
    const preampArray = preamp.trim() ? preamp.split(',').map(item => item.trim()).filter(item => item) : null;
    const dacArray = dac.trim() ? dac.split(',').map(item => item.trim()).filter(item => item) : null;
    const sourcesArray = sources.trim() ? sources.split(',').map(item => item.trim()).filter(item => item) : null;

    if (!speakersArray.length || !amplifierArray.length) {
      setError('Lautsprecher und Verstärker sind erforderlich.');
      setLoading(false);
      return;
    }

    const setupData = {
      userId: currentUser.id,
      speakers: speakersArray,
      amplifier: amplifierArray,
      preamp: preampArray,
      dac: dacArray,
      sources: sourcesArray
    };

    const isExistingSetup = Boolean(setupId);
    const url = `/api/hifisetups/${isExistingSetup ? setupId : 'null'}`;

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        credentials: 'include',
        body: JSON.stringify(setupData)
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Fehler beim Speichern des Setups.');
      }
      if (!isExistingSetup && data.user && data.user.id) {
        setSetupId(data.user.id);
      }
      alert('Setup erfolgreich gespeichert!');
      navigate('/'); // Redirect to main (map) page.
    } catch (err) {
      console.error('Error saving setup:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="setup-form-container">
      <h2>{setupId ? 'Setup bearbeiten' : 'Neues Setup erstellen'}</h2>
      {error && <div className="error-message">{error}</div>}
      
      <form onSubmit={handleSubmit} className="setup-form">
        <div className="form-group">
          <label htmlFor="speakers">
            <SpeakerIcon className="material-icon" />
            Lautsprecher:
          </label>
          <textarea
            id="speakers"
            required
            maxLength="255"
            value={speakers}
            placeholder="Bitte trenne mehrere Einträge durch ein Komma."
            onChange={(e) => setSpeakers(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="amplifier">
            <VolumeUpIcon className="material-icon" />
            Verstärker:
          </label>
          <textarea
            id="amplifier"
            required
            maxLength="255"
            value={amplifier}
            placeholder="Bitte trenne mehrere Einträge durch ein Komma."
            onChange={(e) => setAmplifier(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="preamp">
            <EqualizerIcon className="material-icon" />
            Vorverstärker:
          </label>
          <textarea
            id="preamp"
            maxLength="255"
            value={preamp}
            placeholder="Optional"
            onChange={(e) => setPreamp(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="dac">
            <SettingsInputComponentIcon className="material-icon" />
            DAC:
          </label>
          <textarea
            id="dac"
            maxLength="255"
            value={dac}
            placeholder="Optional"
            onChange={(e) => setDac(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="sources">
            <AlbumIcon className="material-icon" />
            Quellgeräte:
          </label>
          <textarea
            id="sources"
            maxLength="255"
            value={sources}
            placeholder="Optional, bitte trenne mehrere Einträge durch ein Komma."
            onChange={(e) => setSources(e.target.value)}
          />
        </div>
        <button type="submit" disabled={loading}>
          {loading ? 'Wird gespeichert...' : 'Speichern'}
        </button>
      </form>
    </div>
  );
};

export default HiFiSetupForm;